import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SortButton from './SortButton';
import Pick, { PickListHeader } from './Pick';
import { formatExpectedReturn, formatDollarChange, toneOfSigned } from '../utils/returns';
import { formatEventDate, readApiError, savedPick } from '../utils/builder';
import AutoPicksOption from './AutoPicksOption';

const ODIKAI_API_BASE = process.env.REACT_APP_ODIKAI_API_BASE || '/api/odikai';
const MAX_DESCRIPTION_LENGTH = 500;

function sanitizeInput(input) {
  // Remove HTML tags, trim, and limit length
  return input.replace(/<[^>]*>?/gm, '').trim().slice(0, MAX_DESCRIPTION_LENGTH);
}

export default function LanguageBuilder() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [description, setDescription] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState(0); // 0: idle, 1: converting, 2: optimizing, 3: building picks
  const [apiResponse, setApiResponse] = useState(null);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [playbookName, setPlaybookName] = useState('');
  const [autoPicks, setAutoPicks] = useState(true);
  const [sortField, setSortField] = useState('weight');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedSections, setExpandedSections] = useState({ recommendedPicks: false });
  const [optimizeLanguageOutput, setOptimizeLanguageOutput] = useState(null);

  const formatDate = formatEventDate;

  const formatPercent = (value) => {
    if (!value && value !== 0) return 'N/A';
    return `${(value * 100).toFixed(2)}%`;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const cleanDescription = sanitizeInput(description);
    if (!cleanDescription || cleanDescription.length < 50) {
      setError('Please enter a description of at least 50 characters.');
      return;
    }
    setIsLoading(true);
    setLoadingStage(1);
    try {
      // 1. Get strategies from optimize_language
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        navigate('/login');
        return;
      }
      const idToken = await currentUser.getIdToken(true);
      const langRes = await fetch(`${ODIKAI_API_BASE}/optimize_language`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ description: cleanDescription })
      });
      if (!langRes.ok) {
        throw new Error(await readApiError(langRes, 'Failed to match your description to strategies'));
      }
      const langData = await langRes.json();
      setOptimizeLanguageOutput(langData);
      setLoadingStage(2);
      // 2. Optimize
      const optimizeRes = await fetch(`${ODIKAI_API_BASE}/optimize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ strategies: langData.strategies })
      });
      if (!optimizeRes.ok) {
        throw new Error(await readApiError(optimizeRes, 'Failed to optimize portfolio'));
      }
      const optimizeData = await optimizeRes.json();
      setLoadingStage(3);
      // 3. Get picks
      const picksRes = await fetch(`${ODIKAI_API_BASE}/picks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ portfolio: optimizeData.portfolio })
      });
      if (!picksRes.ok) {
        throw new Error(await readApiError(picksRes, 'Failed to get picks'));
      }
      const picksData = await picksRes.json();
      setApiResponse({
        ...optimizeData,
        picks: picksData.picks,
        event_name: picksData.event_name,
        event_date: picksData.event_date,
        portfolio_returns: optimizeData.portfolio_returns,
        previous_year_returns: optimizeData.previous_year_returns,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
      setLoadingStage(0);
    }
  };

  const handleSaveClick = () => {
    setPlaybookName(`Language Playbook ${new Date().toLocaleDateString()}`);
    setShowNameDialog(true);
  };

  const handleSavePlaybook = async () => {
    if (!apiResponse || !user || !playbookName.trim()) return;
    try {
      const playbookId = `${user.uid}_${Date.now()}`;
      const playbookData = {
        is_prebuilt: false, // REQUIRED: Must be false for user-created playbooks
        user_id: user.uid, // REQUIRED: Must match authenticated user
        created_at: new Date().toISOString(),
        last_updated: new Date().toISOString(),
        name: playbookName.trim(),
        is_public: false, // Default to private
        creator_type: 'user',
        event_name: apiResponse.event_name,
        event_date: apiResponse.event_date,
        playbook_type: 'language',
        auto_picks: autoPicks,
        description: sanitizeInput(description),
        optimize_language_output: optimizeLanguageOutput,
        portfolio: apiResponse.portfolio,
        picks: apiResponse.picks.map(pick => savedPick(pick, apiResponse.event_name, apiResponse.event_date)),
        portfolio_returns: apiResponse.portfolio_returns,
        previous_year_returns: apiResponse.previous_year_returns,
      };
      await setDoc(doc(db, 'playbooks', playbookId), playbookData);
      navigate('/playbooks');
    } catch (err) {
      setError('Failed to save playbook: ' + err.message);
    }
  };

  const handleDiscard = () => {
    setApiResponse(null);
    setDescription('');
    setLoadingStage(0);
    setIsLoading(false);
    setError(null);
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };

  if (isLoading) {
    let stageText = '';
    if (loadingStage === 1) stageText = 'Converting your strategy into a playbook...';
    else if (loadingStage === 2) stageText = 'Optimizing your playbook...';
    else if (loadingStage === 3) stageText = 'Building picks...';
    else stageText = 'Building...';
    return (
      <div className="simple-builder-section">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <h2>{stageText}</h2>
          <p>Please wait while we process your strategy.</p>
        </div>
      </div>
    );
  }

  if (apiResponse) {
    return (
      <div className="simple-builder-section">
        <div className="results-view">
          <div className="picks-display">
            <div className="section-header">
              <h2>Your custom playbook</h2>
              <div className="results-summary">
                <div className="summary-item">
                  <h3>Event</h3>
                  <p>{apiResponse.event_name || 'No event selected'}</p>
                  <p className="date">{formatDate(apiResponse.event_date)}</p>
                </div>
                <div className="summary-item">
                  <h3>Expected yearly return</h3>
                  <p className={toneOfSigned(apiResponse.portfolio_returns)}>{formatExpectedReturn(apiResponse.portfolio_returns)}</p>
                </div>
                <div className="summary-item">
                  <h3>Last year on $5,000</h3>
                  <p className={toneOfSigned(apiResponse.previous_year_returns)}>{formatDollarChange(apiResponse.previous_year_returns)}</p>
                </div>
              </div>
            </div>
            <div className="accordion-sections">
              <div className="accordion-item">
                <button 
                  className="accordion-header"
                  onClick={() => toggleSection('playbookDescription')}
                >
                  <h3>Playbook description</h3>
                  {expandedSections.playbookDescription ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </button>
                {expandedSections.playbookDescription && (
                  <div className="accordion-content">
                    <div className="description-section">
                      <p>{sanitizeInput(description)}</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="accordion-item">
                <button 
                  className="accordion-header"
                  onClick={() => toggleSection('recommendedPicks')}
                >
                  <h3>Recommended picks</h3>
                  {expandedSections.recommendedPicks ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </button>
                {expandedSections.recommendedPicks && (
                  <div className="accordion-content">
                    <div className="sort-controls">
                      <SortButton 
                        field="weight" 
                        label="Sort by Weight" 
                        currentField={sortField}
                        direction={sortDirection}
                        onClick={handleSort}
                      />
                      <SortButton 
                        field="odds" 
                        label="Sort by Odds" 
                        currentField={sortField}
                        direction={sortDirection}
                        onClick={handleSort}
                      />
                      <SortButton 
                        field="strategies" 
                        label="Sort by Support" 
                        currentField={sortField}
                        direction={sortDirection}
                        onClick={handleSort}
                      />
                    </div>
                    {apiResponse.picks.length === 0 && (
                      <p className="empty-note">None of your strategies make a pick on this card yet.</p>
                    )}
                    <PickListHeader />
                    {[...apiResponse.picks]
                      .sort((a, b) => {
                        let aValue, bValue;
                        switch (sortField) {
                          case 'weight':
                            aValue = a.augmented_weight;
                            bValue = b.augmented_weight;
                            break;
                          case 'odds':
                            aValue = parseFloat(a.odds);
                            bValue = parseFloat(b.odds);
                            break;
                          case 'strategies':
                            aValue = Array.isArray(a.strategy) ? a.strategy.length : (a.strategy ? 1 : 0);
                            bValue = Array.isArray(b.strategy) ? b.strategy.length : (b.strategy ? 1 : 0);
                            break;
                          default:
                            return 0;
                        }
                        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                      })
                      .map((pick, index) => (
                        <Pick
                          key={index}
                          pick_name={pick.pick}
                          opponent_name={pick.opponent}
                          type={pick.pick_type}
                          weight={pick.augmented_weight}
                          american_odds={pick.odds_us}
                          decimal_odds={parseFloat(pick.odds)}
                          supporting_strategies={Array.isArray(pick.strategy) ? pick.strategy.length : (pick.strategy ? 1 : 0)}
                          total_strategies={optimizeLanguageOutput?.strategies?.length || 10}
                          maxWeight={Math.max(0, ...apiResponse.picks.map(p => p.augmented_weight || 0))}
                        />
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          {error && <div className="error-message">{error}</div>}
          <div className="action-buttons">
            <button className="nav-button back-button" onClick={handleDiscard}>
              Discard
            </button>
            <button className="nav-button next-button" onClick={handleSaveClick}>
              Save playbook
            </button>
          </div>
        </div>
        {showNameDialog && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Name your playbook</h3>
              <input
                type="text"
                value={playbookName}
                maxLength={100}
                onChange={(e) => setPlaybookName(e.target.value)}
                placeholder="Enter playbook name"
                className="playbook-name-input"
              />
              <AutoPicksOption checked={autoPicks} onChange={setAutoPicks} />
              <div className="modal-actions">
                <button 
                  className="nav-button back-button" 
                  onClick={() => setShowNameDialog(false)}
                >
                  Cancel
                </button>
                <button
                  className="nav-button next-button"
                  onClick={() => {
                    setShowNameDialog(false);
                    handleSavePlaybook();
                  }}
                  disabled={!playbookName.trim()}
                >
                  Save playbook
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="simple-builder-section builder-step">
      <form className="language-builder-form" onSubmit={handleSubmit}>
        <div className="strategy-card">
          <h2 className="strategy-title">Describe your UFC betting strategy</h2>
          <p className="strategy-subtext">Write a few sentences about how you would pick winners in the UFC. Be as detailed or as simple as you like.</p>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value.slice(0, MAX_DESCRIPTION_LENGTH))}
            placeholder="E.g. I like to pick fighters who are younger, have a longer reach, and are on a winning streak. I avoid heavy favorites and look for value in underdogs."
            rows={6}
            maxLength={MAX_DESCRIPTION_LENGTH}
            className="description-input"
            aria-label="Your betting strategy"
          />
          <div className="char-count">
            {description.length}/{MAX_DESCRIPTION_LENGTH} characters
          </div>
          {error && <div className="error-message">{error}</div>}
        </div>
        <div className="form-actions">
          <button className="nav-button back-button" type="button" onClick={() => navigate('/playbooks/build')}>
            Back
          </button>
          <button className="nav-button next-button" type="submit" disabled={isLoading}>
            Build playbook
          </button>
        </div>
      </form>
    </div>
  );
} 